import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, HelpCircle, MessageCircle } from 'lucide-react';
import { FAQS } from '../../data/faqs';
import { capture } from '../../lib/posthog';

/**
 * Brand-styled, accessible FAQ accordion.
 * - `compact` (default false): when true, drops the header — useful when embedded inside HelpPage.
 * - `items`: optional override; defaults to the canonical FAQS list.
 */
const FAQSection = ({ compact = false, items, testIdPrefix = 'faq' }) => {
  const data = items || FAQS;
  const [openIndex, setOpenIndex] = useState(0); // first one open by default for visual cue

  const toggle = (i) => {
    setOpenIndex((current) => {
      const next = current === i ? -1 : i;
      if (next !== -1) capture('faq_opened', { index: next, question: data[next].q });
      return next;
    });
  };

  return (
    <section
      className={compact ? '' : 'py-20 bg-white border-t border-border'}
      data-testid="faq-section"
    >
      <div className={compact ? '' : 'max-w-3xl mx-auto px-6 lg:px-8'}>
        {!compact && (
          <div className="text-center mb-10">
            <span className="inline-flex items-center gap-2 bg-[#0F8A7A]/8 text-[#0F8A7A] text-xs font-semibold tracking-wider uppercase px-4 py-1.5 rounded-full">
              <HelpCircle className="h-3.5 w-3.5" />
              Frequently Asked
            </span>
            <h2 className="mt-4 text-3xl sm:text-4xl font-bold text-[#0B1F3A] tracking-tight">
              Questions, answered.
            </h2>
            <p className="mt-3 text-[#475569] text-base leading-relaxed">
              Quick answers to what parents ask us most. Still curious?{' '}
              <Link to="/contact" className="text-[#0F8A7A] font-medium hover:underline">
                Get in touch
              </Link>
              .
            </p>
          </div>
        )}

        <div className="space-y-3">
          {data.map((item, i) => {
            const open = openIndex === i;
            return (
              <div
                key={item.q}
                className={`bg-white border rounded-2xl transition-colors ${
                  open ? 'border-[#0F8A7A]/40 shadow-sm' : 'border-border hover:border-[#0F8A7A]/20'
                }`}
                data-testid={`${testIdPrefix}-item-${i}`}
              >
                <button
                  type="button"
                  onClick={() => toggle(i)}
                  className="w-full text-left flex items-center justify-between gap-4 px-6 py-5"
                  aria-expanded={open}
                  aria-controls={`${testIdPrefix}-panel-${i}`}
                >
                  <span className="font-semibold text-[#0B1F3A] text-base sm:text-lg pr-2">
                    {item.q}
                  </span>
                  <ChevronDown
                    className={`h-5 w-5 flex-shrink-0 transition-transform ${
                      open ? 'rotate-180 text-[#0F8A7A]' : 'text-[#475569]'
                    }`}
                  />
                </button>
                {open && (
                  <div
                    id={`${testIdPrefix}-panel-${i}`}
                    className="px-6 pb-6 text-[#475569] leading-relaxed border-t border-[#0F8A7A]/10 pt-4"
                  >
                    {item.a}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {!compact && (
          <div className="mt-10 flex items-center justify-center gap-3 text-sm text-[#475569]">
            <MessageCircle className="h-4 w-4 text-[#0F8A7A]" />
            <span>
              Can&apos;t find what you need?{' '}
              <Link to="/contact" className="text-[#0F8A7A] font-medium hover:underline">
                Talk to a real human
              </Link>
              .
            </span>
          </div>
        )}
      </div>
    </section>
  );
};

export default FAQSection;
